import { useTranslation } from "react-i18next";
import { Divider, Form, Icon } from "semantic-ui-react";

import { Element } from "components/ReactScroll/index";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { InvoicingSettings } from "modules/DealerLocationDetails/components/DmsSystem/components";
import { isAdmin } from "utils/common";

import type { Dealer, DealerLocation } from "models";
import type { LocationAdminFormPayload } from "modules/DealerLocationDetails";

const FormHelper = createFormHelper<LocationAdminFormPayload>();

type DmsSystemProps = {
  tabId: string;
  location: DealerLocation;
  dealer?: Dealer;
};

export const DmsSystem = ({ tabId, location, dealer }: DmsSystemProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { watch } = FormHelper.useFormContext();
  const isDayplannerEnabled = watch("is_dayplanner_enabled");

  const isAdminUser = Boolean(user && isAdmin(user.role_id));

  return (
    <Element name={tabId} className="dealer-location-section">
      <div className="section-title">
        <div>
          <Icon className="cloud" />
        </div>
        <span className="section-title-text">{t("v8_dms_system", "DMS / System")}</span>
      </div>

      <div className="dealer-location-columns">
        <div className="dealer-location-column">
          <div className="dealer-location-subheading">{t("v8_dms_settings", "DMS Settings")}</div>
          <Form.Field>
            <label>{t("v8_data_source", "Data source")}</label>
            <FormHelper.Field.Input name="dms_location_source" />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="can_add_intervention_elements" label={t("v8_add_intervention_subitems", "Add Intervention subitems")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="track_dms_importing" label={t("v8_track_dms_importing", "Track DMS importing")} />
          </Form.Field>
        </div>

        <div className="dealer-location-column">
          <div className="dealer-location-subheading">{t("v8_lease", "Lease")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox
              name="default_customer_ok_for_dms_lease_intervention"
              label={t("v8_customer_ok_by_default_for_dms_lease_interventions", "Customer OK by Default for DMS Lease Interventions")}
            />
          </Form.Field>
        </div>
      </div>

      <Divider />
      <div className="dealer-location-columns">
        <div className="dealer-location-column">
          <div className="dealer-location-subheading">{t("v8_xentry_configuration", "Xentry configuration")}</div>
          <Form.Field>
            <label>{t("v8_xentry_sub_outlet_id", "Xentry sub outlet ID")}</label>
            <FormHelper.Field.Input name="xentry_sub_outlet_id" />
          </Form.Field>
        </div>

        <div className="dealer-location-column">
          {isAdminUser && (
            <>
              <div className="dealer-location-subheading">
                {t("v8_keyloop_configuration", "Keyloop configuration")}{" "}
                <span className="dealer-location-subheading-note">({t("v8_keyloop_location_notice", "API settings are also required on dealer level for Keyloop")})</span>
              </div>
              <Form.Field>
                <label>{t("v8_business_unit", "Business unit")}</label>
                <FormHelper.Field.Input name="keyloop_business_unit" />
              </Form.Field>
            </>
          )}
        </div>
      </div>

      {isAdminUser && (
        <>
          <Divider />
          <div className="dealer-location-columns">
            <div className="dealer-location-column">
              <div className="dealer-location-subheading">{t("v8_workshop_day_configuration", "Workshop Day Configuration")}</div>
              <Form.Field>
                <FormHelper.Field.Checkbox name="is_dayplanner_enabled" label={t("v8_workshop_day_overview", "Workshop Day Overview")} />
              </Form.Field>
              <Form.Field>
                <FormHelper.Field.Checkbox
                  name="automatically_assign_wo_when_car_check_started"
                  disabled={!isDayplannerEnabled}
                  label={t("v8_assign_on_check_started", "Assign to mechanic on check started")}
                />
              </Form.Field>
              <Form.Field>
                <FormHelper.Field.Checkbox
                  name="automatically_assign_wo_when_intervention_fixed"
                  disabled={!isDayplannerEnabled}
                  label={t("v8_assign_on_intervention_fixed", "Assign to mechanic on intervention fixed")}
                />
              </Form.Field>
            </div>

            <div className="dealer-location-column">
              <InvoicingSettings location={location} dealer={dealer} />
            </div>
          </div>
        </>
      )}
    </Element>
  );
};
